// /ViewManager/ColumnsPane.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import HyperionToolTip from "../../HyperionToolTip";

import DragIndicatorIcon         from "@mui/icons-material/DragIndicator";
import ArrowForwardIcon          from "@mui/icons-material/ArrowForward";
import EditNoteIcon              from "@mui/icons-material/EditNote";
import VisibilityOutlinedIcon    from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlinedIcon          from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon      from "@mui/icons-material/LockOpenOutlined";
import FormatAlignLeftIcon       from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon     from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon      from "@mui/icons-material/FormatAlignRight";
import MoreHorizIcon             from "@mui/icons-material/MoreHoriz";
import TableChartOutlinedIcon    from "@mui/icons-material/TableChartOutlined";

import FieldConfigPanel    from "./FieldConfigPanel";
import ViewFieldJSONDrawer from "./ViewFieldJSONDrawer";
import AISparkleIcon       from "../../../assets/AISparkleIcon";
import "./ColumnsPane.css";
import "./Rows.css";

// ── Align cycle: left → center → right → left ───────────────────────────────
const ALIGN_CYCLE = ["left", "center", "right"];

const AlignIcon = ({ align }) => {
  if (align === "center") return <FormatAlignCenterIcon style={{ fontSize: 14 }} />;
  if (align === "right")  return <FormatAlignRightIcon  style={{ fontSize: 14 }} />;
  return                         <FormatAlignLeftIcon   style={{ fontSize: 14 }} />;
};

// ── NameInput — controlled buffer that stays in sync with prop ───────────────
// Keeps a local copy while the user types; commits on blur or Enter;
// reverts on Escape; re-syncs whenever the prop value changes externally.
const NameInput = ({ fieldKey, headerName, onRename }) => {
  const [localValue, setLocalValue] = useState(headerName || "");

  // If the prop changes externally (e.g. subscription update, view switch),
  // reset the local buffer so we don't write stale data back on blur.
  useEffect(() => {
    setLocalValue(headerName || "");
  }, [headerName]);

  const commit = (value) => {
    const next = value.trim();
    if (next && next !== headerName) {
      onRename(fieldKey, next);
    }
    // Always reset to prop value after commit so the input
    // reflects whatever the parent says the name is.
    setLocalValue(headerName || "");
  };

  return (
    <input
      className="col-chip-name-input"
      aria-label={`rename ${fieldKey}`}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.target.blur();
        }
        if (e.key === "Escape") {
          setLocalValue(headerName || "");
          e.target.blur();
        }
      }}
      placeholder="Column name"
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────

export default function ColumnsPane({
  view,
  fields           = [],
  onRename         = () => {},
  onFieldChange    = () => {},
  onRemoveToPool   = () => {},
  onReorder        = () => {},
  onEditJson       = () => {},
  onApplyFieldJson = () => {},
}) {
  const orderedCols = useMemo(
    () => (fields || []).slice().sort((a, b) => (a.index ?? 0) - (b.index ?? 0)),
    [fields]
  );

  // ── Drag-to-reorder ──────────────────────────────────────────────────
  const dragIndexRef   = useRef(null);
  const overIndexRef   = useRef(null);
  const dragAllowedRef = useRef(false);

  const onDragStart = (idx) => (e) => {
    if (!dragAllowedRef.current) { e.preventDefault(); return; }
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    overIndexRef.current = idx;
  };
  const onDrop = (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    const to   = overIndexRef.current;
    dragIndexRef.current = null;
    overIndexRef.current = null;
    if (from == null || to == null || from === to) return;
    const next = orderedCols.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  // ── FieldConfigPanel — one shared instance ───────────────────────────
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [optionsCol,      setOptionsCol]      = useState(null);

  const openOptions  = (e, col) => { setOptionsAnchorEl(e.currentTarget); setOptionsCol(col); };
  const closeOptions = ()        => { setOptionsAnchorEl(null); setOptionsCol(null); };

  // ── Field JSON drawer ────────────────────────────────────────────────
  const [isFieldJsonOpen, setIsFieldJsonOpen] = useState(false);
  const [fieldJsonTarget, setFieldJsonTarget] = useState(null);

  const activeFieldObj = useMemo(() => {
    const f = String(fieldJsonTarget?.field || "");
    if (!f) return null;
    return orderedCols.find((c) => String(c?.field || "") === f) || null;
  }, [fieldJsonTarget, orderedCols]);

  const closeDrawer = () => { setIsFieldJsonOpen(false); setFieldJsonTarget(null); };

  const requestEditFieldJson = async (fieldName) => {
    const allowed = await onEditJson(fieldName);
    if (allowed === false) return;
    setFieldJsonTarget({ field: fieldName });
    setIsFieldJsonOpen(true);
  };

  const applyFieldJson = async (nextFieldObj) => {
    const targetField = String(fieldJsonTarget?.field || "");
    if (!targetField) return;
    await onApplyFieldJson(targetField, nextFieldObj);
  };

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Box
        className="cv-pane cv-pane--middle"
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <Box className="cv-pane-header">
          <Typography variant="subtitle2">
            Columns in "{view?.viewLabel || "—"}"
          </Typography>
          <span className="cv-pane-header-ai" aria-label="AI features">
            <AISparkleIcon fontSize="large" />
          </span>
        </Box>

        <Box className="col-list cv-scroll">
          {orderedCols.length === 0 && (
            <div className="col-empty">No columns yet. Add from the pool →</div>
          )}

          {orderedCols.map((col, idx) => {
            const isVisible    = !!col.visible;
            const isEditable   = col.editable !== false;
            const chipClass    = [
              "col-chip",
              idx % 2 === 0 ? "col-chip--even" : "col-chip--odd",
              isVisible ? "col-chip--visible" : "col-chip--hidden",
            ].join(" ");

            const currentAlign   = col.align || "left";
            const isDatagridOnly = !!col.datagridOnly;
            const isAiPanel      = !!col.aiPanel;

            return (
              <div
                key={col.field}
                className={chipClass}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDragEnd={() => { dragAllowedRef.current = false; }}
              >
                {/* Drag grip */}
                <span
                  className="col-chip-drag"
                  aria-hidden="true"
                  onMouseDown={() => { dragAllowedRef.current = true; }}
                  onMouseUp={()   => { dragAllowedRef.current = false; }}
                >
                  <DragIndicatorIcon style={{ fontSize: 15 }} />
                </span>

                {/* Index */}
                <span className="col-chip-index" aria-hidden="true">{idx + 1}</span>

                {/* Editable name — isolated component to contain local state */}
                <div className="col-chip-name">
                  <NameInput
                    fieldKey={col.field}
                    headerName={col.headerName}
                    onRename={onRename}
                  />
                </div>

                {/* Separator */}
                <span className="col-chip-sep" aria-hidden="true" />

                {/* Right cluster */}
                <div className="col-chip-right">

                  {isDatagridOnly && (
                    <HyperionToolTip title="Datagrid only — not shown in forms" placement="top" variant="dark">
                      <span className="col-chip-badge col-chip-badge--grid">
                        <TableChartOutlinedIcon style={{ fontSize: 11 }} />
                      </span>
                    </HyperionToolTip>
                  )}

                  {isAiPanel && (
                    <HyperionToolTip title="AI panel field" placement="top" variant="dark">
                      <span className="col-chip-badge col-chip-badge--ai">
                        <AISparkleIcon style={{ fontSize: 11 }} />
                      </span>
                    </HyperionToolTip>
                  )}

                  <HyperionToolTip title={`Align: ${currentAlign}`} placement="top" variant="dark">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const i    = ALIGN_CYCLE.indexOf(currentAlign);
                        const next = ALIGN_CYCLE[(i + 1) % ALIGN_CYCLE.length];
                        onFieldChange(col.field, "align", next);
                      }}
                      aria-label={`alignment: ${currentAlign}`}
                      className="col-chip-action"
                    >
                      <AlignIcon align={currentAlign} />
                    </IconButton>
                  </HyperionToolTip>

                  <HyperionToolTip
                    title={isEditable ? "Editable (click to lock)" : "Read-only (click to unlock)"}
                    placement="top"
                    variant="dark"
                  >
                    <IconButton
                      size="small"
                      onClick={() => onFieldChange(col.field, "editable", !isEditable)}
                      aria-label={isEditable ? "editable" : "read-only"}
                      className="col-chip-action"
                    >
                      {isEditable
                        ? <LockOpenOutlinedIcon style={{ fontSize: 14 }} />
                        : <LockOutlinedIcon     style={{ fontSize: 14 }} />
                      }
                    </IconButton>
                  </HyperionToolTip>

                  <HyperionToolTip
                    title={isVisible ? "Hide column" : "Show column"}
                    placement="top"
                    variant="dark"
                  >
                    <IconButton
                      size="small"
                      onClick={() => onFieldChange(col.field, "visible", !col.visible)}
                      aria-label={`toggle visibility: ${col.field}`}
                      className="col-chip-visibility"
                    >
                      {isVisible
                        ? <VisibilityOutlinedIcon    style={{ fontSize: 15 }} />
                        : <VisibilityOffOutlinedIcon style={{ fontSize: 15 }} />
                      }
                    </IconButton>
                  </HyperionToolTip>

                  <HyperionToolTip title="More options" placement="top" variant="dark">
                    <IconButton
                      size="small"
                      onClick={(e) => openOptions(e, col)}
                      aria-label={`more options: ${col.field}`}
                      className="col-chip-action"
                    >
                      <MoreHorizIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  </HyperionToolTip>

                  <HyperionToolTip title="Edit field JSON" placement="top" variant="dark">
                    <IconButton
                      size="small"
                      onClick={() => requestEditFieldJson(col.field)}
                      aria-label={`edit json: ${col.field}`}
                      className="col-chip-action"
                    >
                      <EditNoteIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  </HyperionToolTip>

                  <HyperionToolTip title="Remove to pool" placement="top" variant="dark">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveToPool(col.field)}
                      aria-label={`remove to pool: ${col.field}`}
                      className="col-chip-action col-chip-remove"
                    >
                      <ArrowForwardIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </HyperionToolTip>

                </div>
              </div>
            );
          })}
        </Box>
      </Box>

      <FieldConfigPanel
        col={optionsCol}
        anchorEl={optionsAnchorEl}
        onClose={closeOptions}
        onFieldChange={onFieldChange}
      />

      {isFieldJsonOpen && activeFieldObj && (
        <div className="vmfd-backdrop" onMouseDown={closeDrawer}>
          <div className="vmfd-shell" onMouseDown={(e) => e.stopPropagation()}>
            <ViewFieldJSONDrawer
              title="Field JSON"
              field={activeFieldObj}
              onClose={closeDrawer}
              onApply={applyFieldJson}
              width={760}
            />
          </div>
        </div>
      )}
    </>
  );
}